import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";

// Fix default marker icons (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const limeIcon = (label) => L.divIcon({
  className: "",
  html: `<div style="background:#84CC16;border:2px solid #0F172A;border-radius:50% 50% 50% 0;width:26px;height:26px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.3)"><span style="transform:rotate(45deg);font-size:12px;font-weight:700;color:#0F172A">${label || ""}</span></div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
  popupAnchor: [0, -26],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) { map.setView([points[0].lat, points[0].lon], 11); }
    else if (points.length > 1) {
      map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

// Geocode a list of {id, location, label, title} → adds lat/lon (single batch request, deduped)
export function useGeocodedJobs(jobs) {
  const [points, setPoints] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      const withLoc = jobs.filter((j) => j.location);
      if (!withLoc.length) { if (active) setPoints([]); return; }
      try {
        const r = await api.post("/geocode/batch", { locations: withLoc.map((j) => j.location) });
        const lookup = r.data || {};
        const out = [];
        for (const j of withLoc) {
          const hit = lookup[j.location.trim()];
          if (hit && hit.found) out.push({ ...j, lat: hit.lat, lon: hit.lon });
        }
        if (active) setPoints(out);
      } catch (e) {
        console.error("Batch geocode failed", e);
        if (active) setPoints([]);
      }
    })();
    return () => { active = false; };
  }, [JSON.stringify(jobs.map((j) => j.id + j.location))]); // eslint-disable-line
  return points;
}

export default function JobMap({ points, height = "360px", className = "" }) {
  if (!points || points.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 rounded-xl text-sm text-slate-400 ${className}`} style={{ height }}>
        No mappable locations
      </div>
    );
  }
  const center = [points[0].lat, points[0].lon];
  return (
    <div className={`rounded-xl overflow-hidden border border-slate-200 ${className}`} style={{ height }} data-testid="job-map">
      <MapContainer center={center} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds points={points} />
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lon]} icon={limeIcon(p.label)}>
            <Popup>
              <Link to={`/jobs/${p.id}`} data-testid={`map-popup-job-${p.id}`}
                style={{ fontWeight: 700, color: "#0F172A", textDecoration: "underline", textUnderlineOffset: 2 }}>
                {p.title}
              </Link>
              <br />{p.location}{p.time ? <><br />{p.time}</> : null}
              {p.pic ? <><br /><span style={{ fontSize: 11 }}><b>PIC:</b> {p.pic}</span></> : null}
              {p.assistants?.length ? <><br /><span style={{ fontSize: 11 }}><b>Crew:</b> {p.assistants.join(", ")}</span></> : null}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
